import { z } from 'zod';

import { getTranslation } from '../../../../utils/get-translation.util';
import { documentSubCategoryByCategory } from '../../constants/document-category.constant';
import { DocumentCategory } from '../../enums/document-category.const';
import { MERGE_FIELD_ISSUE_CODES } from '../../templates/constants/merge-field-issue.constants';
import { TemplateFieldType } from '../../templates/enums/mapping-editor.const';
import { ExpirationPeriod, SigningMode } from '../../templates/enums/template.const';
import { hasFile } from '../../templates/utils/has-file.util';
import { ADD_TEMPLATE_STEP_THREE_MAX_FILE_BYTES } from '../../templates/validations/add-template.validation';

export const QUICK_SIGN_MAX_SIGNATORIES = 10;

export const LINKED_TARGET_NONE_VALUE = '' as const;

const quickSignUploadedFileSchema = z.object({
  abort: z.function().optional(),
  doc: z.instanceof(File),
  docLink: z.string(),
  loadProgress: z.number().optional(),
});

export type QuickSignUploadedFile = z.infer<typeof quickSignUploadedFileSchema>;

export const quickSignStepFourFileField = z
  .union([
    z.instanceof(File, { message: getTranslation('FILE_REQUIRED') }),
    quickSignUploadedFileSchema,
  ])
  .refine((file) => {
    const f = file instanceof File ? file : file.doc;
    return f.size <= ADD_TEMPLATE_STEP_THREE_MAX_FILE_BYTES;
  }, getTranslation('FILE_SIZE_LESS_10_MB'))
  .refine((file) => {
    const f = file instanceof File ? file : file.doc;
    return f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }, getTranslation('ONLY_DOCX_ALLOWED'));

export const quickSignSignatureTabSchema = z.object({
  anchorString: z.string().min(1),
  type: z.enum(TemplateFieldType).optional(),
  value: z.uuid().optional(),
});

export type QuickSignSignatureTab = z.infer<typeof quickSignSignatureTabSchema>;

export const quickSignStakeholderSchema = z.object({
  email: z.string().optional(),
  fullName: z.string().trim().min(1),
  id: z.uuid(),
});

export const quickSignSignatorySlotSchema = z.object({
  slotId: z.uuid(),
  stakeholder: quickSignStakeholderSchema.nullable(),
});

export type QuickSignSignatorySlot = z.infer<typeof quickSignSignatorySlotSchema>;

export const createQuickSignSignatorySlot = (): QuickSignSignatorySlot => ({
  slotId: crypto.randomUUID(),
  stakeholder: null,
});

export const createQuickSignDefaultSignatorySlots = (): QuickSignSignatorySlot[] => [
  createQuickSignSignatorySlot(),
  createQuickSignSignatorySlot(),
];

export const quickSignStepOneShape = z.object({
  name: z
    .string()
    .trim()
    .min(1, getTranslation('DOCUMENT_NAME_REQUIRED'))
    .max(50, getTranslation('DOCUMENT_NAME_EXCEEDS_50')),
  category: z.enum(DocumentCategory).optional(),
  subCategory: z.preprocess(
    (value) => (value === '' || value == null ? undefined : value),
    z.string().optional(),
  ),
  linkedTargetId: z.union([z.literal(LINKED_TARGET_NONE_VALUE), z.uuid()]).optional(),
});

export const quickSignStepOneSchema = quickSignStepOneShape.superRefine((data, ctx) => {
  if (data.category === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('CATEGORY_REQUIRED'),
      path: ['category'],
    });
  }
  if (data.subCategory === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('SUB_CATEGORY_REQUIRED'),
      path: ['subCategory'],
    });
  }
  if (data.category != null && data.subCategory != null) {
    if (data.category === DocumentCategory.OTHER) {
      if (!data.subCategory.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: getTranslation('SUB_CATEGORY_REQUIRED'),
          path: ['subCategory'],
        });
      }
    } else {
      const allowed = documentSubCategoryByCategory[data.category] ?? [];
      if (!allowed.some((option) => option.value === data.subCategory)) {
        ctx.addIssue({
          code: 'custom',
          message: getTranslation('SUB_CATEGORY_REQUIRED'),
          path: ['subCategory'],
        });
      }
    }
  }
});

export const quickSignStepTwoShape = z.object({
  expireDocument: z.boolean(),
  expirationPeriod: z.enum(ExpirationPeriod).optional(),
  signingMode: z.enum(SigningMode).optional(),
  signingOrderMatters: z.boolean(),
});

export const quickSignStepTwoSchema = quickSignStepTwoShape.superRefine((data, ctx) => {
  if (data.signingMode == null) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('SIGNING_MODE_REQUIRED'),
      path: ['signingMode'],
    });
  }
  if (data.expireDocument === true && data.expirationPeriod === undefined) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('EXPIRATION_PERIOD_REQUIRED'),
      path: ['expirationPeriod'],
    });
  }
});

export const quickSignStepThreeShape = z.object({
  signatorySlots: z.array(quickSignSignatorySlotSchema),
});

export const quickSignStepThreeSchema = quickSignStepThreeShape.superRefine((data, ctx) => {
  const assignedCount = data.signatorySlots.filter((slot) => slot.stakeholder != null).length;

  if (assignedCount === 0) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('AT_LEAST_ONE_SIGNATORY'),
      path: ['signatorySlots'],
    });
  }

  if (data.signatorySlots.length > QUICK_SIGN_MAX_SIGNATORIES) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('MAX_10_SIGNATORIES'),
      path: ['signatorySlots'],
    });
  }

  data.signatorySlots.forEach((slot, index) => {
    if (slot.stakeholder == null) {
      ctx.addIssue({
        code: 'custom',
        message: getTranslation('STAKEHOLDER_ASSIGNMENT_REQUIRED'),
        path: ['signatorySlots', index, 'stakeholder'],
      });
    }
  });
});

const mergeFieldIssueSchema = z.object({
  code: z.enum(MERGE_FIELD_ISSUE_CODES),
  field: z.string(),
});

export const quickSignStepFourShape = z.object({
  file: quickSignStepFourFileField.optional(),
  fileParsed: z.boolean().default(false),
  mergeFieldIssues: z.array(mergeFieldIssueSchema).default([]),
  signatureSettingsCompleted: z.boolean().default(false),
  tabs: z.array(quickSignSignatureTabSchema).default([]),
});

export const quickSignStepFourSuperRefine = (
  data: Partial<z.infer<typeof quickSignStepFourShape>>,
  ctx: z.RefinementCtx,
) => {
  if (!hasFile(data.file)) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('FILE_REQUIRED'),
      path: ['file'],
    });
    return;
  }

  const fileValue = data.file;
  if (fileValue != null && typeof fileValue === 'object' && 'docLink' in fileValue) {
    const docLinkTrim = fileValue.docLink?.trim?.() ?? '';
    if (docLinkTrim === '') {
      const uploadPayload = fileValue as { abort?: () => void; loadProgress?: number };
      const s3UploadStillInProgress =
        typeof uploadPayload.abort === 'function' ||
        (uploadPayload.loadProgress !== undefined && uploadPayload.loadProgress < 100);
      if (!s3UploadStillInProgress) {
        ctx.addIssue({
          code: 'custom',
          message: getTranslation('FILE_REQUIRED'),
          path: ['file'],
        });
      }
    }
  }

  if (data.fileParsed !== true) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('FILE_REQUIRED'),
      path: ['fileParsed'],
    });
  }

  const mergeIssues = data.mergeFieldIssues ?? [];
  if (mergeIssues.length > 0) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('MERGE_FIELD_ISSUES_BLOCK_SUBMIT'),
      path: ['mergeFieldIssues'],
    });
  }

  const tabs = data.tabs ?? [];
  if (tabs.length > 0 && tabs.some((tab) => tab.type == null || tab.value == null)) {
    ctx.addIssue({
      code: 'custom',
      message: getTranslation('OPEN_SIGNATURE_SETTINGS_TO_CONTINUE'),
      path: ['tabs'],
    });
  }

  tabs.forEach((tab, index) => {
    if (tab.type == null) {
      ctx.addIssue({
        code: 'custom',
        message: getTranslation('SIGNATURE_FIELD_TYPE_REQUIRED'),
        path: ['tabs', index, 'type'],
      });
    }
    if (tab.value == null) {
      ctx.addIssue({
        code: 'custom',
        message: getTranslation('SIGNATURE_SIGNER_REQUIRED'),
        path: ['tabs', index, 'value'],
      });
    }
  });
};

export const quickSignStepFourSchema = quickSignStepFourShape.superRefine(
  quickSignStepFourSuperRefine,
);

export type QuickSignStepFourFormSchema = z.infer<typeof quickSignStepFourSchema>;

export type QuickSignStepFourFileValue = File | QuickSignUploadedFile;

export const getQuickSignStepFourFile = (value: QuickSignStepFourFileValue | undefined) => {
  if (!value) return undefined;
  return value instanceof File ? value : value.doc;
};

export const getQuickSignStepFourFileLink = (
  value: QuickSignStepFourFileValue | undefined,
): string | undefined => {
  if (!value || typeof value !== 'object' || !('docLink' in value)) return undefined;
  const link = value.docLink;
  return link?.trim() ? link : undefined;
};

export const quickSignFormSchema = z.object({
  stepFour: quickSignStepFourSchema,
  stepOne: quickSignStepOneSchema,
  stepThree: quickSignStepThreeSchema,
  stepTwo: quickSignStepTwoSchema,
});

export type QuickSignFormSchema = z.infer<typeof quickSignFormSchema>;

export const getQuickSignDefaultValues = (): QuickSignFormSchema => ({
  stepOne: {
    name: '',
    category: undefined,
    subCategory: undefined,
    linkedTargetId: undefined,
  },
  stepTwo: {
    expireDocument: false,
    expirationPeriod: undefined,
    signingMode: undefined,
    signingOrderMatters: false,
  },
  stepThree: {
    signatorySlots: createQuickSignDefaultSignatorySlots(),
  },
  stepFour: {
    file: undefined,
    fileParsed: false,
    mergeFieldIssues: [],
    signatureSettingsCompleted: false,
    tabs: [],
  },
});

export const QUICK_SIGN_DEFAULT_VALUES: QuickSignFormSchema = getQuickSignDefaultValues();

const STEP_SCHEMA_BY_NUMBER: Record<number, z.ZodType> = {
  1: z.object({
    stepFour: z.any(),
    stepOne: quickSignStepOneSchema,
    stepThree: z.any(),
    stepTwo: z.any(),
  }),
  2: z.object({
    stepFour: z.any(),
    stepOne: quickSignStepOneShape,
    stepThree: z.any(),
    stepTwo: quickSignStepTwoSchema,
  }),
  3: z.object({
    stepFour: z.any(),
    stepOne: quickSignStepOneShape,
    stepThree: quickSignStepThreeSchema,
    stepTwo: quickSignStepTwoShape,
  }),
  4: z.object({
    stepFour: quickSignStepFourSchema,
    stepOne: quickSignStepOneShape,
    stepThree: quickSignStepThreeShape,
    stepTwo: quickSignStepTwoShape,
  }),
  5: quickSignFormSchema,
};

export const getQuickSignSchemaForStep = (step: number): z.ZodType => {
  return STEP_SCHEMA_BY_NUMBER[step] ?? quickSignFormSchema;
};
